/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { styled, Button, Stack, SvgIcon, useMediaQuery, Theme } from '@mui/material';
import {
  CaseActionList,
  CaseSearchIcon,
  CaseSearchIconActive,
  DashBoardIcon,
  DashBoardIconActive,
  ReportIcon,
  ReportIconActive,
  SettingsIcon,
  SettingsIconActive,
  ChatSvgIcon,
  useNotifierContext,
  UrlService
} from '@oh-vcp/components-ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  EconsultCaseActionItem,
  EconsultReferralErrorDialog,
  EconsultSelectRequesterDialog,
  EconsultUserService,
  useAuth,
  useCmsService,
  useConsultantService,
  useReferralLookup
} from '@oh-vcp/components-web';
import {
  AppConfig,
  CaseDetails,
  CaseDetailsResponsePayloadDTO,
  CaseNote,
  EconsultProviderTypes,
  EconsultStatusTypes,
  EconsultUser,
  EconsultUtils,
  NoteActionTypes,
  NoteStatusCodes,
  NoteTypes,
  Referral
} from '@oh-vcp/components-common';
import CaseService from '@oh-vcp/components-web/src/services/caseService';
import useCaseResults from '../../api/useCaseResults';
import { useEconsultCaseResultContext } from '@oh-vcp/components-web/src/econsultCaseResultContext/EconsultCaseResultContext';
import useAssignerPrograms from '@oh-vcp/components-web/src/services/useAssignerPrograms';
import useFileService from '@oh-vcp/components-web/src/services/useFileService';

const isMobile = EconsultUtils.isMobileDevice() && !EconsultUtils.isIPad();
const RequestConsultButton = styled(Button)(({ theme }) => ({
  width: 200,
  alignSelf: isMobile ? 'center' : 'left',
  '&:hover': {
    backgroundColor: '#77B800'
  },
  left: isMobile ? 0 : 60
}));

interface RequestConsultParams {
  caseSpecialistId?: string | null | undefined;
  delegator?: EconsultUser;
  seenUnavailability?: string | null;
  programType?: string | null | undefined;
  programId?: string | null | undefined;
  specialty?: string | null | undefined;
  subSpecialty?: string | null | undefined;
}

interface EconsultCaseActionPanelProps {
  isDraftChanged: () => boolean | false;
  setUpdatedDraftNote: (updDraftNote: CaseNote | undefined) => void;
  setUpdatedCaseDetails: (updCaseDetails: CaseDetails | undefined) => void;
  getUpdatedDraftNote: () => CaseNote | undefined;
}

const EconsultCaseActionPanelContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.primary.custom2,
  paddingTop: 20,
  height: 'calc(100vh - 5rem)',
  overflow: 'auto',
  [theme.breakpoints.down('lg')]: {
    width: '100vw'
  }
}));

const EconsultCaseActionPanel: FC<EconsultCaseActionPanelProps> = ({
  isDraftChanged,
  setUpdatedDraftNote,
  setUpdatedCaseDetails,
  getUpdatedDraftNote
}) => {
  const { t } = useTranslation();
  const { providerType, statusType } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hasTriage, setHasTriage] = useState<boolean>(false);
  const [hasTriageAssigner, setHasTriageAssigner] = useState<boolean>(false);
  const [hasCons, setHasCons] = useState<boolean>(false);
  const [hasRefs, setHasRefs] = useState<boolean>(false);
  const [isEconsultReferrer, setIsEconsultReferrer] = useState<boolean>(false);
  const [isEconsultConsultant, setIsEconsultConsultant] = useState<boolean>(false);
  const [isEconsultDelegate, setIsEconsultDelegate] = useState<boolean>(false);
  const [isSelectRequesterDialogOpen, setIsSelectRequesterDialogOpen] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [specialistId, setSpecialistId] = useState<string | null>(null);
  const [programId, setProgramId] = useState<string | null>(null);
  const [myConsultants, setMyConsultants] = useState<EconsultUser[]>([]);
  const [delegatorId, setDelegatorId] = useState<string | undefined>(undefined);
  const [requestConsultParams, setRequestConsultParams] = useState<
    RequestConsultParams | undefined
  >(undefined);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const processedReferralId = useRef<string | null>(null);
  const { fetchById } = useReferralLookup(undefined);
  const { fetchById: fetchFileById } = useFileService(undefined);
  const { fetchQuery: fetchHubTopicCaseDetails } = useCmsService(undefined);
  const { data: consultantsData, refetch: refetchConsultants } = useConsultantService();
  const { setDashboardUserRole } = useEconsultCaseResultContext();
  const { addNotification, removeAllErrorNotifications } = useNotifierContext();
  const { data: consultResults } = useCaseResults(
    1,
    25,
    EconsultProviderTypes.consultant.toUpperCase()
  );
  const { data: referrerResults } = useCaseResults(
    1,
    25,
    EconsultProviderTypes.referrer.toUpperCase()
  );
  const { data: assingerPrograms } = useAssignerPrograms();

  const handleOnRequestCaseConsultClicked = useCallback(
    async (
      caseSpecialistId?: string | null | undefined,
      delegator?: EconsultUser,
      seenUnavailability?: string | null,
      programType?: string | null | undefined,
      programId?: string | null | undefined,
      specialty?: string | null | undefined,
      subSpecialty?: string | null | undefined,
      referralId?: string | null | undefined
    ) => {
      console.log('Request Consult clicked -- 1' + caseSpecialistId + ', refId:' + referralId);
      if (user) {
        const { userid, firstName, lastName } = user;
        if (userid && (firstName || lastName)) {
          let response: CaseDetailsResponsePayloadDTO | null = null;
          if (referralId) {
            let referralResponse: Referral | null = null;
            try {
              referralResponse = await fetchById(referralId);
            } catch (error: any) {
              if (
                error &&
                error.response &&
                error.response.data &&
                error.response.data.error &&
                error.response.data.error.indexOf('User Mismatch') > -1
              ) {
                navigate('/error/user');
              } else {
                removeAllErrorNotifications();
                addNotification({
                  type: 'error',
                  message: t('Case.referral.notFound'),
                  id: userid
                });
              }
              return;
            }
            if (referralResponse) {
              const updatedCaseDetails: CaseDetails = {
                externalReferralName: 'eRef_Ocean',
                externalReferralId: referralId,
                isOceanConsult: true,
                statusCd: NoteActionTypes.draft,
                patient: {
                  firstName: referralResponse.patient?.firstName,
                  lastName: referralResponse.patient?.surname,
                  dateOfBirth: referralResponse.patient?.birthDate,
                  gender: referralResponse.patient?.sex?.toLowerCase()?.startsWith('m')
                    ? 'M'
                    : referralResponse.patient?.sex?.toLowerCase()?.startsWith('f')
                    ? 'F'
                    : 'O',
                  ohipNumber: referralResponse.patient?.hn,
                  ohipVersion: referralResponse.patient?.hnVC,
                  noOhip: !referralResponse.patient?.hn
                },
                referrer: {
                  userId: userid,
                  firstName: firstName,
                  lastName: lastName,
                  roles: {
                    econCaseCreate: true
                  }
                }
              };
              if (referralResponse.note) {
                if (referralResponse.note.text) {
                  try {
                    var noteParts = referralResponse.note.text.split('<br>');
                    if (noteParts) {
                      for (var i = 0; i < noteParts.length; i++) {
                        if (noteParts[i].trim() === '<b>EConsult Question(s)</b>') {
                          if (!updatedCaseDetails.notes) {
                            updatedCaseDetails.notes = [];
                            updatedCaseDetails.notes.push({
                              noteTypeCd: NoteTypes.referrer,
                              ownerId: userid,
                              statusCd: NoteActionTypes.draft
                            });
                          }
                          updatedCaseDetails.notes[0].noteContent = noteParts[i + 1].replace(
                            '&nbsp;',
                            ''
                          );
                        }
                      }
                    }
                  } catch (error) {
                    addNotification({
                      type: 'error',
                      message: t('econsult.ocean.errorProcessingNote'),
                      id: userid
                    });
                  }
                }

                if (referralResponse.attachments) {
                  try {
                    for (let i = 0; i < referralResponse.attachments.length; i++) {
                      if (referralResponse.attachments[i].fileId) {
                        const file = await fetchFileById(
                          parseInt(referralResponse.attachments[i].fileId!, 10)
                        );
                        if (file && updatedCaseDetails?.notes?.[0]) {
                          if (!updatedCaseDetails.notes[0].attachments) {
                            updatedCaseDetails.notes[0].attachments = [];
                          }
                          updatedCaseDetails.notes[0].attachments?.push({
                            fileId: file.id,
                            statusCd: NoteStatusCodes.active
                          });
                        }
                      }
                    }
                  } catch (error) {
                    addNotification({
                      type: 'error',
                      message: t('econsult.ocean.couldNotRetrieveAttachments'),
                      id: userid
                    });
                  }
                }
                try {
                  response = await CaseService.upsertCaseDetails(updatedCaseDetails);
                } catch (error: any) {
                  console.log('error 1: ' + error?.response?.data?.error);
                  if (
                    error?.response?.data?.error === 'E_CASE_VALIDATION_ERROR_REFERRER_SPECIALIST'
                  ) {
                    const mdErrMsg = error?.response?.data?.details;
                    addNotification({
                      type: 'error',
                      message: mdErrMsg || t('Case.draft.invalidDelegateRecipent2')
                    });
                  } else {
                    addNotification({
                      type: 'error',
                      message: t('econsult.ocean.failedToCreateCase'),
                      id: userid
                    });
                  }
                }
              }
            } else {
              addNotification({
                type: 'error',
                message: t('Case.referral.notFound'),
                id: userid
              });
              return;
            }
          } else {
            try {
              response = await CaseService.upsertCaseDetails({
                statusCd: NoteActionTypes.draft,
                participant: programId
                  ? {
                      program: {
                        programId: programId
                      },
                      statusCd: NoteStatusCodes.draft
                    }
                  : (caseSpecialistId ? {
                    consultant: {userId: caseSpecialistId},
                    statusCd: NoteStatusCodes.draft
                  } : undefined),
                programType: programType
                  ? programType.toUpperCase() !== 'MANAGED_SPECIALTY_GROUP'
                    ? 'DIRECT'
                    : programType.toUpperCase()
                  : (caseSpecialistId ? 'DIRECT' : null),
                specialty: specialty ? { code: specialty.toUpperCase() } : null,
                subSpecialty: subSpecialty
                  ? {
                      code: specialty ? specialty.toUpperCase() : undefined,
                      subCode: subSpecialty.toUpperCase()
                    }
                  : null,
                referrer: {
                  userId: delegator ? delegator.userId : userid,
                  firstName: delegator ? delegator.firstName : firstName,
                  lastName: delegator ? delegator.lastName : lastName,
                  roles: {
                    econCaseCreate: delegator ? Boolean(delegator.roles?.econCaseCreate) : true
                  }
                },
                notes: [
                  {
                    noteTypeCd: NoteTypes.referrer,
                    ownerId: userid,
                    statusCd: NoteActionTypes.draft
                  }
                ]
              });
            } catch (error: any) {
              console.log('error - ' + error?.response?.data?.error);
              const mdErrMsg = error?.response?.data?.details;
              if (error?.response?.data?.error === 'E_CANNOT_SEND_CASE_TO_PROGRAM') {
                addNotification({
                  type: 'error',
                  message: mdErrMsg || t('econsult.caseCreateModule.error'),
                  id: userid
                });
                return;
              } else if (
                error?.response?.data?.error === 'E_CASE_VALIDATION_ERROR_REFERRER_SPECIALIST'
              ) {
                addNotification({
                  type: 'error',
                  message: mdErrMsg || t('Case.draft.invalidDelegateRecipent2')
                });
              } else {
                addNotification({
                  type: 'error',
                  message: mdErrMsg || t('Case.draft.failedSave')
                });
              }
            }
          }

          if (response?.data?.caseId) {
            const url = `/case/${NoteTypes.referrer.toLowerCase()}/${NoteActionTypes.draft.toLowerCase()}/${
              response.data.caseId
            }${caseSpecialistId ? `?specialistId=${caseSpecialistId}` : ''}${
              seenUnavailability ? `&seenUnavailability=${seenUnavailability}` : ''
            }`;
            navigate(url);
          } else if (referralId) {
            addNotification({
              type: 'error',
              message: t('econsult.ocean.failedToCreateCase'),
              id: userid
            });
          }
        }
      }
    },
    [navigate, user]
  );

  const handleInternalRequestConsult = () => {
    if (myConsultants && myConsultants.length > 1) {
      if (myConsultants.length === 2) {
        handleDelegatorUserSelected(myConsultants[1].userId.toString());
      } else if (myConsultants.length > 2) {
        setIsSelectRequesterDialogOpen(true);
      }
    } else {
      handleOnRequestCaseConsultClicked();
    }
  };

  const handleDelegatorUserSelected = (userId: string) => {
    console.log('handleDelegatorUserSelected...');
    setDelegatorId(userId === '0' ? undefined : userId);
    const delegator = myConsultants.find((d) => d.userId.toString() === userId.toString());
    if (delegator) {
      if (requestConsultParams) {
        const {
          caseSpecialistId,
          seenUnavailability,
          subSpecialty,
          specialty,
          programType,
          programId
        } = requestConsultParams;
        handleOnRequestCaseConsultClicked(
          caseSpecialistId,
          delegator,
          seenUnavailability,
          programType,
          programId,
          specialty,
          subSpecialty
        );
        setRequestConsultParams(undefined);
      } else {
        handleOnRequestCaseConsultClicked(undefined, delegator);
      }
    }
    setIsSelectRequesterDialogOpen(false);
  };

  useEffect(() => {
    let consultants = consultantsData || [];
    consultants = consultants
      .filter((c) => c.roles?.econCaseCreate)
      .sort((a, b) => a.firstName.localeCompare(b.firstName));
    setMyConsultants([
      {
        userId: 0,
        firstName: 'Select requester...'
      },
      ...consultants
    ]);
  }, [consultantsData]);

  const handleHubTopic = async (hubTopic: string) => {
    try {
      const hubTopicCaseDetails = await fetchHubTopicCaseDetails(hubTopic);
      if (hubTopicCaseDetails) {
        const genders = ['M', 'F', 'O'];
        const response = await CaseService.upsertCaseDetails({
          statusCd: NoteActionTypes.draft,
          referrer: {
            firstName: user?.firstName,
            lastName: user?.lastName,
            userId: user?.userid,
            roles: user?.roles
          },
          patient: {
            firstName: hubTopicCaseDetails.patient.firstName,
            lastName: hubTopicCaseDetails.patient.lastName,
            dateOfBirth: hubTopicCaseDetails.patient.dateOfBirth,
            gender: genders.includes(hubTopicCaseDetails.patient.gender?.substring(0, 1) || '')
              ? hubTopicCaseDetails.patient.gender?.substring(0, 1)
              : 'O',
            ohipNumber: hubTopicCaseDetails.patient.healthCardNumber,
            noOhip: !hubTopicCaseDetails.patient.healthCardNumber
          },
          externalReferralName: 'EMR_CMS',
          externalReferralId: hubTopic
        });

        if (response?.data?.caseId) {
          const url = `/case/${NoteTypes.referrer.toLowerCase()}/${NoteActionTypes.draft.toLowerCase()}/${
            response.data.caseId
          }`;
          navigate(url);
        }
      }
    } catch (e: any) {
      console.error(e);
      const errCode = e?.response?.data?.error;
      const mdErrMsg = e?.response?.data?.details;
      if (
        errCode && errCode === 'E_CASE_VALIDATION_ERROR_REFERRER_SPECIALIST'
      ) {
        addNotification({
          type: 'error',
          message: mdErrMsg || t('Case.draft.invalidDelegateRecipent2')
        });
      } else {
        addNotification({
          type: 'error',
          message: mdErrMsg || t('Case.draft.failedSave')
        });
      }
    }
  };

  useEffect(() => {
    if (!user) return;
    const handleDraftCaseFromUrl = async () => {
      const params = {
        specialistId: UrlService.getParamIgnoreCase(searchParams, 'specialistId'),
        seenUnavailability: UrlService.getParamIgnoreCase(searchParams, 'seenUnavailability'),
        programType: UrlService.getParamIgnoreCase(searchParams, 'programType'),
        programId: UrlService.getParamIgnoreCase(searchParams, 'programId'),
        specialty: UrlService.getParamIgnoreCase(searchParams, 'specialty'),
        subSpecialty: UrlService.getParamIgnoreCase(searchParams, 'subSpecialty'),
        referralId:
          UrlService.getParamIgnoreCase(searchParams, 'referralId') ||
          sessionStorage.getItem('referralId'),
        hubTopic: UrlService.getParamIgnoreCase(searchParams, 'hubtopic'),
        create: UrlService.getParamIgnoreCase(searchParams, 'create')
      };

      const specialistChanged = params.specialistId && specialistId !== params.specialistId;
      const programChanged = params.programId && programId !== params.programId;
      const referralId = params.referralId;
      const hubTopic = params.hubTopic;

      if (hubTopic) {
        await handleHubTopic(hubTopic);
        return;
      }

      if (referralId && processedReferralId.current !== referralId) {
        processedReferralId.current = referralId;
        await handleOnRequestCaseConsultClicked(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          referralId
        );
        sessionStorage.removeItem('referralId');
      } else {
        if (specialistChanged) setSpecialistId(params.specialistId);
        if (programChanged) setProgramId(params.programId);

        if (specialistChanged || programChanged) {
          const delegatorConsultants = await refetchConsultants();
          if (delegatorConsultants.data && delegatorConsultants.data.length > 0) {
            const filteredDelegatorConsultants = delegatorConsultants.data.filter(
              (c) => c.roles?.econCaseCreate
            );
            if (filteredDelegatorConsultants.length === 1) {
              await handleOnRequestCaseConsultClicked(
                params.specialistId,
                delegatorConsultants.data[1],
                params.seenUnavailability,
                params.programType,
                params.programId,
                params.specialty,
                params.subSpecialty
              );
            } else if (filteredDelegatorConsultants.length > 1) {
              setRequestConsultParams({
                caseSpecialistId: params.specialistId,
                delegator: undefined,
                seenUnavailability: params.seenUnavailability,
                programType: params.programType,
                programId: params.programId,
                specialty: params.specialty,
                subSpecialty: params.subSpecialty
              });
              setIsSelectRequesterDialogOpen(true);
            }
          } else {
            await handleOnRequestCaseConsultClicked(
              params.specialistId,
              undefined,
              params.seenUnavailability,
              params.programType,
              params.programId,
              params.specialty,
              params.subSpecialty
            );
          }
        } else if (params.create !== null && params.create !== undefined) {
          handleInternalRequestConsult();
        }
      }
    };
    handleDraftCaseFromUrl();
  }, [
    handleOnRequestCaseConsultClicked,
    searchParams,
    specialistId,
    programId,
    myConsultants,
    user
  ]);

  useEffect(() => {
    let url = '';
    if ((consultResults && consultResults.totalCount > 0) || isEconsultConsultant) {
      setDashboardUserRole('CONSULTANT');
      url = `/case/${NoteTypes.consultant}/needsaction`;
      const hasTriageCases = EconsultUserService.hasTriage(consultResults?.data || []);
      const hasConCases = EconsultUserService.hasCons(consultResults?.data || []);
      const hasRefCases = EconsultUserService.hasRefs(consultResults?.data || []);
      setHasTriage(hasTriageCases);
      setHasCons(hasConCases);
      setHasRefs(hasRefCases);
    } else if (
      consultResults &&
      consultResults.totalCount === 0 &&
      referrerResults &&
      referrerResults.totalCount > 0
    ) {
      setDashboardUserRole('REFERRER');
      url = `/case/${NoteTypes.referrer}/needsaction`;
      const hasTriageCases = EconsultUserService.hasTriage(referrerResults.data);
      const hasConCases = EconsultUserService.hasCons(referrerResults.data);
      const hasRefCases = EconsultUserService.hasRefs(referrerResults.data);
      setHasTriage(hasTriageCases);
      setHasCons(hasConCases);
      setHasRefs(hasRefCases);
    } else if (isEconsultReferrer) {
      url = `/case/${NoteTypes.referrer}/needsaction`;
    }

    // If mobile view do not redirect to the default inbox
    if (!providerType && !statusType && !isMobile) {
      navigate(url.toLowerCase());
    }
  }, [
    consultResults,
    setDashboardUserRole,
    navigate,
    providerType,
    statusType,
    isMobile,
    referrerResults,
    isEconsultConsultant,
    isEconsultReferrer
  ]);

  useEffect(() => {
    if (consultResults && consultResults.data && assingerPrograms) {
      const hasTriageAssignerCases = EconsultUserService.hasTriageAssigner(
        consultResults.data,
        assingerPrograms
      );
      setHasTriageAssigner(hasTriageAssignerCases);
    }
  }, [assingerPrograms, consultResults]);

  useEffect(() => {
    if (user) {
      const isReferrer = EconsultUserService.isEconsultReferrer(user);
      const isConsultant = EconsultUserService.isEconsultConsultant(user);
      const isDelegate = EconsultUserService.isEconsultDelegate(user);
      setIsEconsultReferrer(isReferrer || false);
      setIsEconsultConsultant(isConsultant || false);
      setIsEconsultDelegate(isDelegate || false);
    }
  }, [user]);

  useEffect(() => {
    const count = (consultResults?.consultantCount || 0) + (referrerResults?.referrerCount || 0);
    if (count > 0) {
      document.title = `eConsult (${count})`;
    }
  }, [consultResults, referrerResults, isEconsultConsultant, isEconsultReferrer]);

  const handleSelectedRequesterCancel = () => {
    const specialistId = UrlService.getParamIgnoreCase(searchParams, 'specialistId');
    const programId = UrlService.getParamIgnoreCase(searchParams, 'programId');
    setIsSelectRequesterDialogOpen(false);
    if (specialistId || programId) {
      navigate('/case/nodelegate');
    }
  };

  return (
    <>
      <EconsultCaseActionPanelContainer direction="column">
        {user?.roles?.econCaseCreate && (
          <RequestConsultButton
            variant="contained"
            color="primary"
            onClick={() => handleInternalRequestConsult()}>
            <SvgIcon component={ChatSvgIcon} inheritViewBox sx={{ mr: 1 }} />
            Request Consult
          </RequestConsultButton>
        )}
        <CaseActionList>
          {(hasCons || isEconsultConsultant || hasTriage) && (
            <>
              <EconsultCaseActionItem
                icon={DashBoardIcon}
                activeIcon={DashBoardIconActive}
                isSubItem={false}
                text={t('Case.panel.allConsults')}
                url="/case/consultant/all"
                active={
                  (!providerType || providerType === EconsultProviderTypes.consultant) &&
                  (!statusType || statusType === EconsultStatusTypes.all)
                }
                isDraftChanged={isDraftChanged}
                setUpdatedDraftNote={setUpdatedDraftNote}
                setUpdatedCaseDetails={setUpdatedCaseDetails}
                getUpdatedDraftNote={getUpdatedDraftNote}
              />
              <EconsultCaseActionItem
                isSubItem={true}
                text={t('Case.panel.needsAttention')}
                url="/case/consultant/needsaction"
                active={
                  providerType === EconsultProviderTypes.consultant &&
                  statusType === EconsultStatusTypes.needsAction
                }
                count={consultResults?.consultantCount}
                isDraftChanged={isDraftChanged}
                setUpdatedDraftNote={setUpdatedDraftNote}
                setUpdatedCaseDetails={setUpdatedCaseDetails}
                getUpdatedDraftNote={getUpdatedDraftNote}
              />
              {(!hasTriage || hasCons) && (
                <>
                  <EconsultCaseActionItem
                    isSubItem={true}
                    text={t('Case.panel.waitingForMoreInfo')}
                    url="/case/consultant/waiting"
                    active={
                      providerType === EconsultProviderTypes.consultant &&
                      statusType === EconsultStatusTypes.waiting
                    }
                    isDraftChanged={isDraftChanged}
                    setUpdatedDraftNote={setUpdatedDraftNote}
                    setUpdatedCaseDetails={setUpdatedCaseDetails}
                    getUpdatedDraftNote={getUpdatedDraftNote}
                  />
                  <EconsultCaseActionItem
                    isSubItem={true}
                    text={t('Case.panel.consultProvided')}
                    url="/case/consultant/provided"
                    active={
                      providerType === EconsultProviderTypes.consultant &&
                      statusType === EconsultStatusTypes.provided
                    }
                    isDraftChanged={isDraftChanged}
                    setUpdatedDraftNote={setUpdatedDraftNote}
                    setUpdatedCaseDetails={setUpdatedCaseDetails}
                    getUpdatedDraftNote={getUpdatedDraftNote}
                  />
                  <EconsultCaseActionItem
                    isSubItem={true}
                    text={t('Case.panel.consultReturned')}
                    url="/case/consultant/declined"
                    active={
                      providerType === EconsultProviderTypes.consultant &&
                      statusType === EconsultStatusTypes.declined
                    }
                    isDraftChanged={isDraftChanged}
                    setUpdatedDraftNote={setUpdatedDraftNote}
                    setUpdatedCaseDetails={setUpdatedCaseDetails}
                    getUpdatedDraftNote={getUpdatedDraftNote}
                  />
                </>
              )}
            </>
          )}
          {user?.roles?.econCaseCreate && (
            <>
              <EconsultCaseActionItem
                icon={DashBoardIcon}
                activeIcon={DashBoardIconActive}
                isSubItem={false}
                text={t('Case.panel.allRequests')}
                url="/case/referrer/all"
                active={
                  providerType === EconsultProviderTypes.referrer &&
                  statusType === EconsultStatusTypes.all
                }
                isDraftChanged={isDraftChanged}
                setUpdatedDraftNote={setUpdatedDraftNote}
                setUpdatedCaseDetails={setUpdatedCaseDetails}
                getUpdatedDraftNote={getUpdatedDraftNote}
              />
              <EconsultCaseActionItem
                isSubItem={true}
                text={t('Case.panel.needsAttention')}
                url="/case/referrer/needsaction"
                active={
                  providerType === EconsultProviderTypes.referrer &&
                  statusType === EconsultStatusTypes.needsAction
                }
                count={consultResults?.referrerCount}
                isDraftChanged={isDraftChanged}
                setUpdatedDraftNote={setUpdatedDraftNote}
                setUpdatedCaseDetails={setUpdatedCaseDetails}
                getUpdatedDraftNote={getUpdatedDraftNote}
              />
              <EconsultCaseActionItem
                isSubItem={true}
                text={t('Case.panel.waitingForResponse')}
                url="/case/referrer/waiting"
                active={
                  providerType === EconsultProviderTypes.referrer &&
                  statusType === EconsultStatusTypes.waiting
                }
                isDraftChanged={isDraftChanged}
                setUpdatedDraftNote={setUpdatedDraftNote}
                setUpdatedCaseDetails={setUpdatedCaseDetails}
                getUpdatedDraftNote={getUpdatedDraftNote}
              />
              <EconsultCaseActionItem
                isSubItem={true}
                text={t('Case.panel.completed')}
                url="/case/referrer/completed"
                active={
                  providerType === EconsultProviderTypes.referrer &&
                  statusType === EconsultStatusTypes.completed
                }
                isDraftChanged={isDraftChanged}
                setUpdatedDraftNote={setUpdatedDraftNote}
                setUpdatedCaseDetails={setUpdatedCaseDetails}
                getUpdatedDraftNote={getUpdatedDraftNote}
              />
              <EconsultCaseActionItem
                isSubItem={true}
                text={t('Case.panel.cancelled')}
                url="/case/referrer/cancelled"
                active={
                  providerType === EconsultProviderTypes.referrer &&
                  statusType === EconsultStatusTypes.cancelled
                }
                isDraftChanged={isDraftChanged}
                setUpdatedDraftNote={setUpdatedDraftNote}
                setUpdatedCaseDetails={setUpdatedCaseDetails}
                getUpdatedDraftNote={getUpdatedDraftNote}
              />
              <EconsultCaseActionItem
                isSubItem={true}
                text={t('Case.panel.drafts')}
                url="/case/referrer/draft"
                active={
                  providerType === EconsultProviderTypes.referrer &&
                  statusType === EconsultStatusTypes.draft
                }
                isDraftChanged={isDraftChanged}
                setUpdatedDraftNote={setUpdatedDraftNote}
                setUpdatedCaseDetails={setUpdatedCaseDetails}
                getUpdatedDraftNote={getUpdatedDraftNote}
              />
            </>
          )}
          {(hasCons || isEconsultConsultant || hasRefs || hasTriageAssigner) && (
            <EconsultCaseActionItem
              icon={ReportIcon}
              activeIcon={ReportIconActive}
              isSubItem={false}
              text={t('Case.panel.reports')}
              url="/case/reports"
              active={providerType === 'reports'}
              isDraftChanged={isDraftChanged}
              setUpdatedDraftNote={setUpdatedDraftNote}
              setUpdatedCaseDetails={setUpdatedCaseDetails}
              getUpdatedDraftNote={getUpdatedDraftNote}
            />
          )}
          <EconsultCaseActionItem
            icon={CaseSearchIcon}
            activeIcon={CaseSearchIconActive}
            isSubItem={false}
            text={t('Case.panel.search')}
            url="/case/search"
            active={providerType === 'search'}
            isDraftChanged={isDraftChanged}
            setUpdatedDraftNote={setUpdatedDraftNote}
            setUpdatedCaseDetails={setUpdatedCaseDetails}
            getUpdatedDraftNote={getUpdatedDraftNote}
          />
          {(isEconsultReferrer || isEconsultConsultant || isEconsultDelegate) && (
            <EconsultCaseActionItem
              icon={SettingsIcon}
              activeIcon={SettingsIconActive}
              isSubItem={false}
              externalUrl={`${AppConfig.baseSelfServeUrl}/#/serviceSettings/econsult/general`}
              text={t('Case.panel.settings')}
              isDraftChanged={isDraftChanged}
              setUpdatedDraftNote={setUpdatedDraftNote}
              setUpdatedCaseDetails={setUpdatedCaseDetails}
              getUpdatedDraftNote={getUpdatedDraftNote}
            />
          )}
        </CaseActionList>
      </EconsultCaseActionPanelContainer>
      <EconsultSelectRequesterDialog
        isDialogOpen={isSelectRequesterDialogOpen}
        options={myConsultants}
        onConfirm={handleDelegatorUserSelected}
        onCancel={handleSelectedRequesterCancel}
        userId={delegatorId || '0'}
      />
    </>
  );
};

export default EconsultCaseActionPanel;
